// src/utils/report-styles.js — DropPin report layout style registry.
// ---------------------------------------------------------------------
// v0.103.0 — REPORT LAYOUT STYLES. Six named styles, each a bundle of
// presentation tokens consumed by PdfShell (cover / page header /
// section headings / footer) and defect-report-template (per-defect
// item layout). Styles change PRESENTATION ONLY — every style carries
// the same data: defect number, description, photos, priority, status,
// trade, location and plan reference.
//
// 'signature' is the existing DropPin look and stays the DEFAULT:
// its `tokens` is null, which makes PdfShell + the template take the
// exact same code paths they took before styles existed. Nothing
// changes for users until they pick a different style.
//
// Token schema (all optional — absent = current behaviour):
//   cover: {
//     themeKey      — base palette key into COVER_THEMES ('light'|'mono'|'cinematic')
//     bands         — override bands; color may be the string 'ACCENT'
//     accentStrip   — override the right-edge brand strip on/off
//     accentRuleH   — mm height of the rule under the doc label (0 = none)
//     ruleFullWidth — accent rule spans the content width, not 28mm
//     font          — 'helvetica' | 'times' for label + title
//     titleSize     — pt
//     titleAlign    — 'left' | 'center'
//     brandRowRgb   — wordmark row text colour ([r,g,b] | 'ACCENT')
//     dotColor      — pin-head colour ([r,g,b] | 'ACCENT' | 'BLUE')
//     wordmarkBg    — mask colour behind the typed "i" ([r,g,b] | 'ACCENT')
//     chipStyle     — 'ghost' | 'solid' | 'outline' | 'none'
//     kpiStyle      — 'cells' | 'plain' | 'bars'
//     labelPill     — doc label sits inside an accent-filled pill
//     formalRules   — double hairlines above label + below title (classic)
//   }
//   header:  { font, rule: 'accent'|'grey'|'double', ruleW, compact }
//   heading: { font, size, deco: 'rule'|'bar'|'underline'|'double'|'plain', caps }
//   footer:  { font, italic, rule: 'hair'|'accent'|'none', size }
//   body:    { font }
//   item:    'card' | 'airy' | 'boldPhoto' | 'row' | 'modernCard' | 'ruled'

export const REPORT_STYLES = {
  signature: {
    key: 'signature',
    name: 'Signature',
    blurb: 'The DropPin original — cinematic dark cover, card-style defect blocks.',
    item: 'card',
    tokens: null, // null = legacy pipeline, byte-for-byte the pre-style output
  },

  professional: {
    key: 'professional',
    name: 'Professional',
    blurb: 'Restrained and quiet — serif headings, generous whitespace, hairline rules.',
    item: 'airy',
    tokens: {
      cover: {
        themeKey: 'light',
        font: 'times',
        titleSize: 30,
        accentStrip: false,
        accentRuleH: 0.5,
        ruleFullWidth: true,
        dotColor: 'ACCENT',
        chipStyle: 'none',
        kpiStyle: 'plain',
      },
      header:  { font: 'times', rule: 'grey', ruleW: 0.3 },
      heading: { font: 'times', deco: 'rule', size: 14 },
      footer:  { font: 'times', rule: 'hair' },
      body:    { font: 'helvetica' },
      item: 'airy',
    },
  },

  bold: {
    key: 'bold',
    name: 'Bold',
    blurb: 'Loud and confident — heavy titles, strong accent bars, big photos.',
    item: 'boldPhoto',
    tokens: {
      cover: {
        themeKey: 'light',
        bands: [
          { yFrac: 0.30, color: 'ACCENT' },
          { yFrac: 1.00, color: [255, 255, 255] },
        ],
        brandRowRgb: [255, 255, 255],
        wordmarkBg: 'ACCENT',
        dotColor: [15, 23, 42],
        titleSize: 40,
        accentStrip: false,
        accentRuleH: 1.6,
        chipStyle: 'solid',
        kpiStyle: 'bars',
      },
      header:  { rule: 'accent', ruleW: 1.6 },
      heading: { deco: 'bar', size: 15 },
      footer:  { rule: 'accent' },
      item: 'boldPhoto',
    },
  },

  compact: {
    key: 'compact',
    name: 'Compact',
    blurb: 'Dense and tabular — one row per defect, the most items per page.',
    item: 'row',
    tokens: {
      cover: {
        themeKey: 'light',
        bands: [{ yFrac: 1.0, color: [255, 255, 255] }],
        titleSize: 22,
        accentStrip: false,
        accentRuleH: 0.8,
        dotColor: 'ACCENT',
        chipStyle: 'outline',
        kpiStyle: 'plain',
      },
      header:  { rule: 'grey', ruleW: 0.3, compact: true },
      heading: { deco: 'rule', size: 11 },
      footer:  { rule: 'hair', size: 7 },
      item: 'row',
    },
  },

  modern: {
    key: 'modern',
    name: 'Modern',
    blurb: 'Clean SaaS look — soft cards, colour chips, rounded corners.',
    item: 'modernCard',
    tokens: {
      cover: {
        themeKey: 'light',
        bands: [{ yFrac: 1.0, color: [248, 250, 252] }],
        titleSize: 34,
        accentStrip: false,
        accentRuleH: 0,
        dotColor: 'ACCENT',
        chipStyle: 'solid',
        kpiStyle: 'cells',
        labelPill: true,
      },
      header:  { rule: 'accent', ruleW: 0.6 },
      heading: { deco: 'underline', size: 14 },
      footer:  { rule: 'hair' },
      item: 'modernCard',
    },
  },

  classic: {
    key: 'classic',
    name: 'Classic',
    blurb: 'Formal and ruled — serif throughout, label/value fields, no fills.',
    item: 'ruled',
    tokens: {
      cover: {
        themeKey: 'mono',
        font: 'times',
        titleSize: 28,
        titleAlign: 'center',
        accentStrip: false,
        accentRuleH: 0,
        dotColor: 'ACCENT',
        chipStyle: 'none',
        kpiStyle: 'plain',
        formalRules: true,
      },
      header:  { font: 'times', rule: 'double', ruleW: 0.3 },
      heading: { font: 'times', deco: 'double', size: 13, caps: true },
      footer:  { font: 'times', italic: true, rule: 'hair' },
      body:    { font: 'times' },
      item: 'ruled',
    },
  },
};

export const REPORT_STYLE_ORDER = ['signature', 'professional', 'bold', 'compact', 'modern', 'classic'];

export function resolveReportStyle(key) {
  return REPORT_STYLES[key] || REPORT_STYLES.signature;
}

// ---------------------------------------------------------------------
// Curated accent swatches for the style chooser. First entry is the
// suite default (InSite construction orange — matches DEFAULT_BRAND
// via tokens.css --brand-600). These are PDF-output colours, not app
// UI colours, so literal hex is correct here.
export const CURATED_COLORS = [
  { hex: '#e8650a', label: 'Suite orange' },
  { hex: '#fb923c', label: 'DropPin orange' },
  { hex: '#0f4c81', label: 'Marine blue' },
  { hex: '#1d4ed8', label: 'Royal blue' },
  { hex: '#0f766e', label: 'Teal' },
  { hex: '#15803d', label: 'Forest green' },
  { hex: '#9d174d', label: 'Maroon' },
  { hex: '#334155', label: 'Charcoal' },
];

// ---------------------------------------------------------------------
// Sticky user preference — style + accent colour survive across
// reports and reloads. Follows the app's droppin.* localStorage
// convention (droppin.showArchivedReports, droppin.user.v1, ...).
// Saved-report settingsJson (server-side, per report) still overrides
// this when a ?reportId row carries its own styleKey.
const LS_KEY = 'droppin.reportStyle.v1';

export function loadStylePrefs() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    const out = {};
    if (parsed && typeof parsed.styleKey === 'string' && REPORT_STYLES[parsed.styleKey]) {
      out.styleKey = parsed.styleKey;
    }
    if (parsed && typeof parsed.brandColor === 'string' && /^#[0-9a-fA-F]{6}$/.test(parsed.brandColor)) {
      out.brandColor = parsed.brandColor;
    }
    return out;
  } catch { return {}; }
}

export function saveStylePrefs({ styleKey, brandColor }) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify({
      styleKey: styleKey || 'signature',
      brandColor: brandColor || null,
    }));
  } catch { /* private mode etc. — preference just won't stick */ }
}
